import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useStudyPlans } from '../../hooks/useStudyPlans';
import { createStudentAccount, getCreateAccountErrorMessage } from '../../utils/createStudentAccount';
import { SubscriptionExistsError, getDaysRemaining } from '../../utils/subscriptions';
import { notifyStudentEnrolled } from '../../utils/notifications';
import PaymentReceipt from '../payments/PaymentReceipt';

function generateTempPassword() {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';
  let pwd = '';
  for (let i = 0; i < 10; i++) pwd += chars[Math.floor(Math.random() * chars.length)];
  return pwd;
}

/**
 * Modal for granting student access from an inquiry — temp password, fee mode, invoice.
 */
export default function GrantAccessModal({ inquiry, onClose, onSuccess }) {
  const { currentUser } = useAuth();
  const { plans: studyPlans } = useStudyPlans();
  const planOptions = studyPlans.length > 0 ? studyPlans : [];

  const [form, setForm] = useState({
    password: generateTempPassword(),
    planId: inquiry?.planId || planOptions[0]?.id || '1-month',
    paymentMethod: 'online',
    paymentDate: new Date().toISOString().slice(0, 10),
    amountPaid: inquiry?.planPrice ? String(inquiry.planPrice) : '',
    packageTotal: inquiry?.planPrice ? String(inquiry.planPrice) : '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  if (!inquiry) return null;

  async function handleSubmit(e) {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const selectedPlan = planOptions.find((p) => p.id === form.planId);
      const payment = {
        method: form.paymentMethod,
        date: form.paymentDate,
        amount: Number(form.amountPaid) || selectedPlan?.price || 0,
        packageTotal: Number(form.packageTotal) || Number(form.amountPaid) || selectedPlan?.price || 0,
        currency: 'USD',
        createInvoice: true,
      };

      const created = await createStudentAccount({
        studentName: inquiry.fullName,
        email: inquiry.email,
        password: form.password,
        planId: form.planId,
        planDays: selectedPlan?.days,
        planLabel: selectedPlan?.name || inquiry.planLabel,
        createdBy: currentUser?.uid || '',
        payment,
      });

      if (created.uid) {
        await notifyStudentEnrolled({
          userId: created.uid,
          registrationNumber: created.registrationNumber,
          planLabel: selectedPlan?.name || inquiry.planLabel,
        }).catch(console.warn);
      }

      setResult({
        credentials: {
          email: created.email,
          password: form.password,
          registrationNumber: created.registrationNumber,
          name: created.displayName,
        },
        payment: created.payment,
      });

      onSuccess?.({
        registrationNumber: created.registrationNumber,
        payment: created.payment,
        uid: created.uid,
      });
    } catch (err) {
      if (err instanceof SubscriptionExistsError) {
        const existing = err.existing;
        setError(
          `This student already has an active ${existing.planLabel || 'subscription'} `
          + `(${getDaysRemaining(existing)} days left).`
        );
      } else {
        setError(getCreateAccountErrorMessage(err));
      }
      console.error(err);
    } finally {
      setSaving(false);
    }
  }

  async function copyCredentials() {
    if (!result?.credentials) return;
    const c = result.credentials;
    const text = [
      'Success Bridge Academy login',
      c.registrationNumber ? `Registration No: ${c.registrationNumber}` : null,
      `Email: ${c.email}`,
      `Password: ${c.password}`,
    ]
      .filter(Boolean)
      .join('\n');
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      /* ignore */
    }
  }

  return (
    <div style={s.overlay} onClick={onClose}>
      <div style={s.modal} onClick={(e) => e.stopPropagation()}>
        <div style={s.head}>
          <h3 style={s.title}>Grant access — {inquiry.fullName}</h3>
          <button type="button" onClick={onClose} style={s.closeBtn} aria-label="Close">
            <span className="material-symbols-outlined">close</span>
          </button>
        </div>

        {result ? (
          <div style={s.body}>
            <div style={s.successBanner}>Access granted successfully!</div>
            <div style={s.credsBox}>
              <p style={s.credsTitle}>Login credentials</p>
              {result.credentials.registrationNumber ? (
                <p><strong>Registration No:</strong> {result.credentials.registrationNumber}</p>
              ) : null}
              <p><strong>Email:</strong> {result.credentials.email}</p>
              <p><strong>Password:</strong> {result.credentials.password}</p>
              <button type="button" onClick={copyCredentials} style={s.copyBtn}>
                Copy login details
              </button>
            </div>
            {result.payment ? (
              <div style={s.invoiceBox}>
                <h4 style={s.invoiceTitle}>Invoice / Receipt</h4>
                <PaymentReceipt payment={result.payment} printId="grant-access-invoice" />
              </div>
            ) : null}
            <button type="button" onClick={onClose} style={s.doneBtn}>
              Done
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} style={s.body}>
            <p style={s.sub}>
              Create student account for <strong>{inquiry.email}</strong> with temporary password and payment invoice.
            </p>

            {error ? <div style={s.errorBanner}>{error}</div> : null}

            <label style={s.label}>
              Temporary password
              <div style={{ display: 'flex', gap: 8 }}>
                <input
                  style={s.input}
                  value={form.password}
                  onChange={(e) => setForm((p) => ({ ...p, password: e.target.value }))}
                  required
                  minLength={6}
                />
                <button
                  type="button"
                  style={s.secondaryBtn}
                  onClick={() => setForm((p) => ({ ...p, password: generateTempPassword() }))}
                >
                  Generate
                </button>
              </div>
            </label>

            <label style={s.label}>
              Study plan
              <select
                style={s.input}
                value={form.planId}
                onChange={(e) => setForm((p) => ({ ...p, planId: e.target.value }))}
              >
                {planOptions.map((plan) => (
                  <option key={plan.id} value={plan.id}>
                    {plan.name} ({plan.days} days)
                  </option>
                ))}
              </select>
            </label>

            <div style={s.paymentSection}>
              <p style={s.paymentTitle}>Payment / Fee mode</p>
              <div style={s.grid}>
                <label style={s.label}>
                  Mode
                  <select
                    style={s.input}
                    value={form.paymentMethod}
                    onChange={(e) => setForm((p) => ({ ...p, paymentMethod: e.target.value }))}
                  >
                    <option value="online">Online banking</option>
                    <option value="cash">Cash</option>
                  </select>
                </label>
                <label style={s.label}>
                  Payment date
                  <input
                    type="date"
                    style={s.input}
                    value={form.paymentDate}
                    onChange={(e) => setForm((p) => ({ ...p, paymentDate: e.target.value }))}
                  />
                </label>
                <label style={s.label}>
                  Amount paid (USD)
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    style={s.input}
                    value={form.amountPaid}
                    onChange={(e) => setForm((p) => ({ ...p, amountPaid: e.target.value }))}
                  />
                </label>
                <label style={s.label}>
                  Package total (USD)
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    style={s.input}
                    value={form.packageTotal}
                    onChange={(e) => setForm((p) => ({ ...p, packageTotal: e.target.value }))}
                  />
                </label>
              </div>
            </div>

            <div style={s.actions}>
              <button type="button" onClick={onClose} style={s.secondaryBtn}>
                Cancel
              </button>
              <button type="submit" disabled={saving} style={s.primaryBtn(saving)}>
                {saving ? 'Creating account…' : 'Grant access & create invoice'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

const s = {
  overlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(15,23,42,0.55)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999,
    padding: 16,
  },
  modal: {
    background: '#fff',
    borderRadius: 16,
    width: '100%',
    maxWidth: 560,
    maxHeight: '90vh',
    overflowY: 'auto',
    boxShadow: '0 24px 64px rgba(0,0,0,0.2)',
  },
  head: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '18px 22px',
    borderBottom: '1px solid #e2e8f0',
  },
  title: { margin: 0, fontSize: 17, fontWeight: 800, color: '#4b00d1' },
  closeBtn: { background: 'none', border: 'none', cursor: 'pointer', color: '#64748b' },
  body: { padding: '20px 22px' },
  sub: { fontSize: 13, color: '#64748b', marginBottom: 16, lineHeight: 1.6 },
  label: { display: 'flex', flexDirection: 'column', gap: 6, fontSize: 12, fontWeight: 700, color: '#475569', marginBottom: 12 },
  input: {
    padding: '10px 12px',
    borderRadius: 10,
    border: '1.5px solid #e2e8f0',
    fontSize: 14,
  },
  paymentSection: {
    background: '#f8fafc',
    borderRadius: 12,
    padding: 14,
    marginBottom: 16,
    border: '1px dashed #cbd5e1',
  },
  paymentTitle: { margin: '0 0 10px', fontSize: 12, fontWeight: 800, color: '#475569' },
  grid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 },
  actions: { display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 8 },
  primaryBtn: (disabled) => ({
    padding: '11px 18px',
    borderRadius: 10,
    border: 'none',
    background: disabled ? '#94a3b8' : '#059669',
    color: '#fff',
    fontWeight: 700,
    fontSize: 13,
    cursor: disabled ? 'not-allowed' : 'pointer',
  }),
  secondaryBtn: {
    padding: '10px 14px',
    borderRadius: 10,
    border: '1.5px solid #e2e8f0',
    background: '#fff',
    color: '#475569',
    fontWeight: 700,
    fontSize: 13,
    cursor: 'pointer',
  },
  errorBanner: {
    background: '#fef2f2',
    border: '1px solid #fecaca',
    color: '#b91c1c',
    padding: '10px 14px',
    borderRadius: 10,
    fontSize: 13,
    marginBottom: 14,
  },
  successBanner: {
    background: '#ecfdf5',
    border: '1px solid #a7f3d0',
    color: '#047857',
    padding: '10px 14px',
    borderRadius: 10,
    fontSize: 13,
    marginBottom: 14,
    fontWeight: 600,
  },
  credsBox: {
    background: '#eef2ff',
    borderRadius: 12,
    padding: 14,
    marginBottom: 16,
    fontSize: 13,
  },
  credsTitle: { fontWeight: 800, color: '#4b00d1', marginBottom: 8 },
  copyBtn: {
    marginTop: 10,
    padding: '8px 14px',
    borderRadius: 8,
    border: 'none',
    background: '#4b00d1',
    color: '#fff',
    fontWeight: 700,
    fontSize: 12,
    cursor: 'pointer',
  },
  invoiceBox: { marginBottom: 16 },
  invoiceTitle: { margin: '0 0 10px', fontSize: 15, fontWeight: 800, color: '#1b365d' },
  doneBtn: {
    width: '100%',
    padding: '12px',
    borderRadius: 10,
    border: 'none',
    background: '#4b00d1',
    color: '#fff',
    fontWeight: 700,
    cursor: 'pointer',
  },
};
